import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Search as SearchIcon, X, Hash, User, Folder, ArrowRight } from 'lucide-react';
import api from '../config/api';
import { getAvatarUrl } from '../utils/imageUtils';
import VerifiedAvatar from './VerifiedAvatar';

const DEBOUNCE_MS = 200;

const SearchSpotlight = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState({ profiles: [], tags: [], categories: [] });
  const [activeIndex, setActiveIndex] = useState(0);
  const flatListRef = useRef([]);

  // Build a flat render list with section headers
  const buildFlat = useCallback((sug) => {
    const out = [];
    const pushSection = (title, items, type) => {
      if (!items || items.length === 0) return;
      out.push({ kind: 'header', title });
      items.forEach((item) => out.push({ kind: type, item }));
    };
    pushSection('Профили', sug.profiles, 'profile');
    pushSection('Категории', sug.categories, 'category');
    pushSection('Теги', (sug.tags || []).map((t) => ({ tag: t })), 'tag');
    return out;
  }, []);

  // Fetch suggestions (debounced)
  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length === 0) {
      setSuggestions({ profiles: [], tags: [], categories: [] });
      flatListRef.current = [];
      setActiveIndex(0);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await api.get(`/api/search/suggest?q=${encodeURIComponent(query.trim())}`);
        if (cancelled) return;
        const data = res?.data || { profiles: [], tags: [], categories: [] };
        setSuggestions(data);
        flatListRef.current = buildFlat(data);
        // set first interactive row as active if exists
        const idx = flatListRef.current.findIndex((r) => r.kind !== 'header');
        setActiveIndex(idx >= 0 ? idx : 0);
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      cancelled = true;
    };
  }, [query, open, buildFlat]);

  // Focus the input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        try { inputRef.current?.focus(); } catch (_) {}
      }, 20);
    } else {
      setQuery('');
      setSuggestions({ profiles: [], tags: [], categories: [] });
      flatListRef.current = [];
      setActiveIndex(0);
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return;
    }
    const list = flatListRef.current || [];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (list.length === 0) return;
      let i = activeIndex;
      do {
        i = Math.min(list.length - 1, i + 1);
      } while (i < list.length && list[i]?.kind === 'header' && i < list.length - 1);
      setActiveIndex(i);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (list.length === 0) return;
      let i = activeIndex;
      do {
        i = Math.max(0, i - 1);
      } while (i > 0 && list[i]?.kind === 'header');
      setActiveIndex(i);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = list[activeIndex];
      if (!row) return;
      navigateRow(row);
    }
  };

  const navigateRow = (row) => {
    if (!row || row.kind === 'header') return;

    if (row.kind === 'profile') {
      // Robust: always use legacy route by ID, which redirects to the canonical SEO URL.
      // (Avoids broken /user/:slug links.)
      const p = row.item;
      if (p?.id) navigate(`/user/${encodeURIComponent(String(p.id))}`);
      onClose?.();
      return;
    }

    if (row.kind === 'category') {
      // Category filter -> feed (profiles list)
      const c = row.item;
      // Feed uses ?category=<slug> now.
      if (c?.slug) navigate(`/feed?category=${encodeURIComponent(String(c.slug))}`);
      else navigate('/feed');
      onClose?.();
      return;
    }

    if (row.kind === 'tag') {
      navigate(`/tag/${encodeURIComponent(row.item.tag)}`);
      onClose?.();
    }
  };

  const goFullSearch = (type = 'all') => {
    const q = (query || '').trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}&type=${encodeURIComponent(type)}`);
    onClose?.();
  };

  if (!open) return null;

  // Simple backdrop and sheet effect
  return createPortal(
    (
      <div
        className="fixed inset-0 z-[60] flex items-start justify-center"
        onKeyDown={handleKeyDown}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Panel */}
        <div className="relative w-[92%] sm:w-[640px] mt-16 rounded-2xl border border-white/30 bg-white/80 backdrop-blur-md shadow-2xl overflow-hidden">
          {/* Input row */}
          <div className="flex items-center px-3 sm:px-4 py-3 border-b border-white/40 bg-white/70">
            <SearchIcon className="w-5 h-5 text-secondary-700 mr-2" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск профилей, категорий, тегов..."
              className="flex-1 bg-transparent focus:outline-none placeholder-secondary-400 text-secondary-900 text-sm sm:text-base"
            />
            {busy && <span className="text-xs text-secondary-500 mr-2">Поиск...</span>}
            <button
              className="p-2 rounded-md hover:bg-white/60 transition-colors"
              aria-label="Close"
              title="Закрыть"
              onClick={onClose}
            >
              <X className="w-5 h-5 text-secondary-700" />
            </button>
          </div>

          {/* Suggestions */}
          <div className="max-h-[60vh] overflow-auto p-2 sm:p-3">
            {/* No query info */}
            {!query && (
              <div className="text-center py-8 text-secondary-600 text-sm">
                Введите запрос для поиска
              </div>
            )}

            {query && flatListRef.current.length === 0 && !busy && (
              <div className="text-center py-8 text-secondary-600 text-sm">
                Ничего не найдено
              </div>
            )}

            {flatListRef.current.map((row, idx) => {
              if (row.kind === 'header') {
                return (
                  <div key={`h-${row.title}-${idx}`} className="px-2 pt-3 pb-1 text-[11px] uppercase tracking-wider text-secondary-500">
                    {row.title}
                  </div>
                );
              }
              const active = idx === activeIndex;
              if (row.kind === 'profile') {
                const u = row.item;
                return (
                  <button
                    key={`u-${u.id}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => navigateRow(row)}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                  >
                    <div className="mr-3 flex-shrink-0">
                      <VerifiedAvatar
                        user={u}
                        userId={u?.id}
                        src={u?.avatar_url}
                        alt={u?.username}
                        size={40}
                        fallbackText={(u?.username || 'U').charAt(0)}
                        badgePositionClass="bottom-0 right-0"
                      />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-secondary-900 line-clamp-1">{u.title ? u.title : `@${u.username}`}</div>
                      <div className="text-xs text-secondary-500 line-clamp-1">@{u.username}</div>
                    </div>
                    {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                  </button>
                );
              }
              if (row.kind === 'category') {
                const c = row.item;
                return (
                  <button
                    key={`c-${c.id}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => navigateRow(row)}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 mr-3 flex items-center justify-center flex-shrink-0">
                      <Folder className="w-5 h-5 text-secondary-400" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-secondary-900 line-clamp-1">{c.name}</div>
                      <div className="text-xs text-secondary-500 line-clamp-1">Категория</div>
                    </div>
                    {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                  </button>
                );
              }
              if (row.kind === 'tag') {
                const t = row.item.tag;
                return (
                  <button
                    key={`t-${t}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => navigateRow(row)}
                    className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 mr-3 flex items-center justify-center flex-shrink-0">
                      <Hash className="w-5 h-5 text-secondary-400" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-secondary-900 line-clamp-1">#{t}</div>
                      <div className="text-xs text-secondary-500">Тег</div>
                    </div>
                    {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                  </button>
                );
              }
              return null;
            })}
          </div>

          {/* Footer actions */}
          <div className="px-3 sm:px-4 py-2 border-t border-white/40 bg-white/70 flex items-center justify-between text-xs text-secondary-500">
            <div className="hidden sm:flex items-center space-x-3">
              <span><kbd className="px-1.5 py-0.5 rounded bg-secondary-100 border border-secondary-200">↑</kbd> <kbd className="px-1.5 py-0.5 rounded bg-secondary-100 border border-secondary-200">↓</kbd> навигация</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-secondary-100 border border-secondary-200">Enter</kbd> открыть</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-secondary-100 border border-secondary-200">Esc</kbd> закрыть</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => goFullSearch('all')}
                className="px-2 py-1 rounded-md bg-primary-500 text-white hover:bg-primary-600"
              >
                Полный поиск
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    document.body
  );
};

export default SearchSpotlight;
